import React from "react";
import { useState, useEffect } from "react";
import "./FinancialInsights.scss";
import CompanyDocs from "../CompanyDocs/CompanyDocs";
import Table from "./Table";
import axios from "axios";
import BalanceSheetGraph from "./BalanceSheetGraph";
import CashFlowGraph from "./CashFlowGraph";
import ProfitLossGraph from "./ProfitLossGraph";
import QuarterlyResultsGraph from "./QuarterlyResultsGraph";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";

const fetchData = async (tickerId, typeofData) => {
  return await axios.get(
    `${process.env.REACT_APP_COMPANY_DOC}/${tickerId}/financial_statements_and_insight/${typeofData}`,
  );
};

const FinancialInsights = () => {
  const [activeTab, setActiveTab] = useState("Balance Sheet");

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const [insightsData, setInsightsData] = useState(null);
  const [cashflowData, setCashflowData] = useState(null);
  const [profitAndLossData, setProfitAndLossData] = useState(null);
  const [balanceSheetData, setBalanceSheetData] = useState(null);
  const [quarterlyResultData, setQuarterlyResultData] = useState(null);
  const [graphData, setGraphData] = useState(null);

  const tickerId = localStorage.getItem("tickerId");

  useEffect(() => {
    (async () => {
      try {
        const insightResponse = await fetchData(tickerId, "insights.json");
        setInsightsData(insightResponse.data);

        const cashflowResponse = await fetchData(tickerId, "cash_flows.json");
        setCashflowData(cashflowResponse.data);

        const balanceSheetResponse = await fetchData(
          tickerId,
          "balance_sheet.json",
        );
        setBalanceSheetData(balanceSheetResponse.data);

        const profitAndLossResponse = await fetchData(
          tickerId,
          "profit_and_loss.json",
        );
        setProfitAndLossData(profitAndLossResponse.data);

        const quarterlyResultResponse = await fetchData(
          tickerId,
          "quarterly_results.json",
        );
        setQuarterlyResultData(quarterlyResultResponse.data);

        const graphResponse = await fetchData(
          tickerId,
          "graph_coordinates.json",
        );
        setGraphData(graphResponse.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    })();
  }, [tickerId]);

  const activeInsights =
    insightsData?.insights?.[
      `insight_${activeTab.toLowerCase().replace(/ /g, "_")}`
    ];

  const dataMapping = {
    "Balance Sheet": balanceSheetData,
    "Cash Flow": cashflowData,
    "Profit and Loss": profitAndLossData,
    "Quarterly Result": quarterlyResultData,
  };

  const graphComponents = {
    "Balance Sheet": BalanceSheetGraph,
    "Cash Flow": CashFlowGraph,
    "Profit and Loss": ProfitLossGraph,
    "Quarterly Result": QuarterlyResultsGraph,
  };

  const tabs = [
    "Balance Sheet",
    "Cash Flow",
    "Profit and Loss",
    "Quarterly Result",
    "Shareholding Pattern",
  ];

  const activeData = dataMapping[activeTab];

  const activeGraphComponent = graphComponents[activeTab];

  const analysisSentences = activeInsights?.Analysis?.split(". ");
  const analysisBulletPoints = analysisSentences
    ?.slice(0, -1)
    ?.map((sentence, index) => <li key={index}>{sentence.trim()}</li>);
  const lastSentence = analysisSentences?.[analysisSentences.length - 1];

  const reportCharacter = (activeInsights?.["Reports Character"] || activeInsights?.["Reports character"] || "").toLowerCase();
  const isPositive = reportCharacter.includes("positive");
  const isNegative = reportCharacter.includes("negative");

  // Background color no longer needed — handled by CSS classes
  let backgroundColor = "white";
  let border = "1px solid #e8edf4";
  switch (reportCharacter) {
    case "positive report":
      break;
    case "negative report":
      break;
    default:
      border = "1px solid #ccc";
  }

  return (
    <div className="container-fluid">
      <Stack
        direction="row"
        spacing={1}
        sx={{
          flexWrap: "wrap",

          padding: "5px",
          marginBottom: "15px",
          width: "fit-content",
          borderRadius: "5px",
          backgroundColor: "#f5f5f5",
        }}
      >
        {tabs.map((tab) => (
          <Chip
            key={tab}
            label={tab}
            clickable
            onClick={() => handleTabClick(tab)}
            color={activeTab === tab ? "primary" : "default"}
            variant={activeTab === tab ? "filled" : "outlined"}
            sx={{
              fontWeight: activeTab === tab ? 700 : 500,
              cursor: "pointer",
              borderRadius: "8px",
              border: "none",
              backgroundColor: activeTab === tab ? "#1a2f4e" : "transparent",
              color: activeTab === tab ? "#ffffff" : "#64748b",
              fontSize: "13px",
              fontFamily: "'DM Sans', sans-serif",
              padding: "0 4px",
              boxShadow: activeTab === tab ? "0 2px 8px rgba(26,47,78,0.25)" : "none",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: activeTab === tab ? "#243d63" : "#eef1f9",
                color: activeTab === tab ? "#ffffff" : "#1a2f4e",
              },
            }}
          />
        ))}
      </Stack>
      <div className="financial-insights-container">
        {activeTab === "Shareholding Pattern" ? (
          <CompanyDocs />
        ) : (
          // <div className="financial-panel">
          //   <div className="financial-insights-top">
          //     <div className="table-container">
          //       <Table data={activeData} />
          //     </div>
          //     <div
          //       className="insights-container"
          //       style={{ backgroundColor: backgroundColor, border: border }}
          //     >
          //       <h2>Insights</h2>
          //       <div className="report-summary">
          //         <ul>{analysisBulletPoints}</ul>
          //         <p>{lastSentence}</p>
          //       </div>
          //     </div>
          //   </div>
          //   <div className="financial-insights-bottom">
          //     <div className="graphs-container">
          //       {activeGraphComponent
          //         ? React.createElement(activeGraphComponent, {
          //             graphData: graphData,
          //           })
          //         : null}
          //     </div>
          //   </div>
          // </div>
          <div className="financial-panel">
            <div className="financial-insights-top">
              <div className="table-container">
                <Table data={activeData} activeTab={activeTab} />
              </div>
              <div
                className={`insights-container ${isPositive ? "insights--positive" : isNegative ? "insights--negative" : "insights--neutral"}`}
              >
                <div className="insights-header">
                  <h2>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" /></svg>
                    Insights
                  </h2>
                  {reportCharacter && (
                    <span className={`report-badge ${isPositive ? "report-badge--positive" : isNegative ? "report-badge--negative" : "report-badge--neutral"}`}>
                      <span className="report-badge-dot" />
                      {isPositive ? "Positive" : isNegative ? "Negative" : "Neutral"}
                    </span>
                  )}
                </div>
                <div className="report-summary">
                  {analysisBulletPoints && analysisBulletPoints.length > 0 ? (
                    <ul>{analysisBulletPoints}</ul>
                  ) : (
                    <p className="no-data">NO DATA FOUND.</p>
                  )}
                  {activeInsights?.Summary && (
                    <div className="report-summary-text">
                      <p>{activeInsights.Summary}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="financial-insights-bottom">
              <div className="graphs-container">
                {activeGraphComponent
                  ? React.createElement(activeGraphComponent, {
                      graphData: graphData,
                    })
                  : null}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FinancialInsights;
